import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageWithCard from '../../components/infrastructure/PageWithCard';
import TableWithHeadingAndGlobalSearch from '../../components/Table/TableWithHeadingAndGlobalSearch';
import PaginationClassic from '../../components/infrastructure/pagination/PaginationClassic';
import PrimaryButton from '../../components/infrastructure/Buttons/PrimaryButton';
import DangerButton from '../../components/infrastructure/Buttons/DangerButton';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment-timezone';
import QueryString from 'qs';
import { authAxiosInstance } from '../../utils/axiosConfig';
import { buildRange, exportCsv, generateEmployeeOptions } from '../../utils/Utils';
import { ClipLoader } from 'react-spinners';
import { getAuth } from '../../app/reducers/Auth/authSlice';
import FormikSelectGroup from '../../components/formik/FormikSelectGroup';
import { useFormik } from 'formik';
import { fetchEmployees, getEmployees } from '../../app/reducers/Users/EmployeeSlice';
import FormikAsyncSelect from '../../components/formik/FormikAsyncSelect';
import FormikInputDateGroup from '../../components/formik/FormikInputDateGroup';
import TableWithHeadingAndSearch from '../../components/Table/TableWithHeadingAndSearch';
import { toast } from 'react-toastify';
import SecondaryButton from '../../components/infrastructure/Buttons/SecondaryButton';

const ViewVisitForm = () => {
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [visitForms, setVisitForms] = useState({ docs: [], totalDocs: 0, totalPages: 0, pagingCounter: 0 });
    const [loading, setLoading] = useState(false);
    const { user } = useSelector(getAuth);
    const { employees } = useSelector(getEmployees);
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [hasQuery, setHasQuery] = useState(() => { return !!searchParams.get('sq') });

    const formik = useFormik({
        initialValues: {
            from: '',
            to: '',
            company: '',
            customer: '',
        },
        onSubmit: (() => {
            try {
                fetchVisitForms()
            } catch (error) {
                console.log("error", error)
                toast.error('failed to fetch visit form')
            }
        })
    })
    const fetchVisitForms = async () => {
        try {
            setLoading(true);
            const payload = {
                populate: true,
                page,
                limit: 100,
                optimize: {
                    ...(formik?.values?.company ? { company: formik.values.company } : {}),
                    ...(formik?.values?.createdBy ? { createdBy: formik.values.createdBy } : {}),
                },
                date: buildRange(formik.values.from, formik.values.to)
            };
            const string = QueryString.stringify(payload);
            const response = await authAxiosInstance.get(`/visitForm?${string}`);
            setVisitForms(response?.data?.data ?? { docs: [], totalDocs: 0, totalPages: 0, pagingCounter: 0 });
            const queryString = btoa(JSON.stringify({
                payload
            }))
            navigate(`/visitForm/view-visit-form?sq=${queryString}`)
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (hasQuery) return
        fetchVisitForms();
    }, [page]);

    const columns = [
        {
            Header: 'SRNO',
            Cell: (prop) => {
                const { row } = prop;
                return <div>{row.index + visitForms?.pagingCounter}</div>;
            },
        },
        {
            Header: 'Date',
            Cell: ({ row }) => {
                return (
                    <div>
                        {row?.original?.date
                            ? moment(row?.original?.date).format(
                                'DD/MM/YYYY HH:mm'
                            )
                            : '--'}
                    </div>
                );
            },
        },
        {
            Header: 'Visit Form No',
            accessor: 'visitForm_no',
        },
        {
            Header: 'Company',
            Cell: ({ row }) => {
                return (
                    <div className="w-[150px]">
                        <p className="text-sm font-semibold text-gray-800">
                            {row?.original?.company?.name || '--'}
                        </p>
                    </div>
                );
            },
        },
        {
            Header: 'Material Condition',
            accessor: 'materialCondition',
        },
        {
            Header: 'Shifts',
            accessor: 'shifts',
        },
        {
            Header: 'Remark',
            Cell: ({ row }) => {
                return (
                    <div className="w-[200px]">
                        <p className="text-wrap">{row?.original?.resolution || '--'}</p>
                    </div>
                );
            },
        },
        {
            Header: 'Photos',
            Cell: ({ row }) => {
                const firstPhoto = row.original?.photos?.[0];
                return (
                    <div className="w-[100px]">
                        {firstPhoto ? (
                            <a
                                href={`${process.env.REACT_APP_S3_URL}${firstPhoto}`}
                                target="_blank"
                            >
                                <img
                                    src={`${process.env.REACT_APP_S3_URL}${firstPhoto}`}
                                />
                            </a>
                        ) : (
                            '--'
                        )}
                    </div>
                );
            },
        },
        {
            Header: 'Videos',
            Cell: ({ row }) => {
                const firstVideo = row.original?.videos?.[0];
                return (
                    <div className="w-[100px]">
                        {firstVideo ? (
                            <a
                                className="text-indigo-500 underline"
                                href={`${process.env.REACT_APP_S3_URL}${firstVideo}`}
                                target="_blank"
                            >
                                <p>Video</p>
                            </a>
                        ) : (
                            '--'
                        )}
                    </div>
                );
            },
        },
        {
            Header: 'Action',
            Cell: ({ row }) => {
                return (
                    <div className="flex flex-col gap-2">
                        <PrimaryButton
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/visitForm/edit-visit-form/${row?.original?._id}`);
                            }}
                            type="button"
                        >
                            Edit
                        </PrimaryButton>
                        <DangerButton
                            onClick={async (e) => {
                                e.stopPropagation();
                                const confirm = window.confirm('Are you sure you want to delete this visit form?');
                                if (confirm) {
                                    try {
                                        await authAxiosInstance.delete(`/visitForm/${row?.original?._id}`);
                                        fetchVisitForms();
                                    } catch (error) {
                                        console.log(error);
                                    }
                                }
                            }}
                            type="button"
                        >
                            Delete
                        </DangerButton>
                    </div>
                );
            },
        },
    ];

    const visitFormMemoData = useMemo(() => visitForms?.docs ?? [], [visitForms]);

    useEffect(() => {
        dispatch(fetchEmployees({ limit: 500 }));
    }, []);
    useEffect(async () => {
        const queryString = searchParams.get('sq');
        if (!queryString) return

        try {
            const decode = JSON.parse(atob(queryString))?.payload
            console.log("decode", decode)
            if (decode?.date?.$gte) {
                formik.setFieldValue('from', decode?.date?.$gte)
            } else {
                formik.setFieldValue('from', '')
            }
            if (decode?.date?.$lte) {
                formik.setFieldValue('to', decode?.date?.$lte)
            } else {
                formik.setFieldValue('to', '')
            }

            if (decode?.optimize?.createdBy) {
                formik.setFieldValue('createdBy', decode?.optimize?.createdBy)
            } else {
                formik.setFieldValue('createdBy', '')
            }
            if (decode?.optimize?.company) {
                formik.setFieldValue('company', decode?.optimize?.company)
            } else {
                formik.setFieldValue('company', '')
            }
            const string = QueryString.stringify(decode);
            const response = await authAxiosInstance.get(`/visitForm?${string}`);
            setVisitForms(response?.data?.data ?? { docs: [], totalDocs: 0, totalPages: 0, pagingCounter: 0 });
        } catch (error) {
            console.log('Error', error)
        } finally {
        }
    }, [searchParams])
    return (
        <div className="p-4">

            <div className="mb-4">
                <form
                    onSubmit={formik.handleSubmit}
                    className="flex flex-col gap-4"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="w-full">
                            <FormikAsyncSelect
                                name={'company'}
                                label='Company'
                                formik={formik}
                                getOptions={async (value) => {
                                    try {
                                        const payload = {
                                            search: value,
                                            approvedStatus: 'approved',
                                        };
                                        const string = QueryString.stringify(payload);
                                        const response = await authAxiosInstance.get(
                                            `/company?${string}`
                                        );
                                        return (
                                            response?.data?.data?.docs?.map((ele) => ({
                                                label: ele.name,
                                                value: ele._id,
                                            })) || []
                                        );
                                    } catch (error) {
                                        console.log(error);
                                        return [];
                                    }
                                }}
                            />
                        </div>
                        <FormikSelectGroup
                            name={'createdBy'}
                            label='Created By'
                            formik={formik}
                            options={generateEmployeeOptions(employees?.docs)}
                        />
                        <FormikInputDateGroup
                            formik={formik}
                            label='From'
                            name={'from'}
                        />
                        <FormikInputDateGroup
                            formik={formik}
                            label='To'
                            name={'to'}
                        />
                    </div>
                    <div className="flex gap-2 ">
                        <PrimaryButton type='submit'>
                            Submit
                        </PrimaryButton>
                    </div>
                </form>
                <p
                    onClick={async () => {
                        try {
                            navigate('/company/reporting')
                            // const resp = await authAxiosInstance.get(`/company/reporting`,)
                        } catch (error) {

                        }
                    }}
                >Reporting</p>
            </div>
            <TableWithHeadingAndSearch
                heading="View Visit Forms"
                loading={loading}
                columns={columns}
                data={visitFormMemoData}
            />
            <PaginationClassic
                paginationDetails={{
                    ...visitForms,
                    docs: visitForms.docs,
                }}
                setPage={setPage}
            />
        </div>
    );
};

export default ViewVisitForm;
